import { Injectable, LOCALE_ID, Inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { ENV } from '../environment';

export interface Language {
  code: string;
  name: string;
  nativeName: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private currentLanguageSubject = new BehaviorSubject<string>('en-US');
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  readonly supportedLanguages: Language[] = [
    { code: 'en-US', name: 'English', nativeName: 'English' },
    { code: 'es', name: 'Spanish', nativeName: 'Español' },
    { code: 'fr', name: 'French', nativeName: 'Français' },
    { code: 'de', name: 'German', nativeName: 'Deutsch' },
    { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
    { code: 'el', name: 'Greek', nativeName: 'Ελληνικά' },
    { code: 'hu', name: 'Hungarian', nativeName: 'Magyar' },
    { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina' },
    { code: 'sl', name: 'Slovene', nativeName: 'Slovenščina' },
    { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski' },
    { code: 'bg', name: 'Bulgarian', nativeName: 'Български' },
    { code: 'et', name: 'Estonian', nativeName: 'Eesti' },
    { code: 'fi', name: 'Finnish', nativeName: 'Suomi' },
    { code: 'ga', name: 'Irish', nativeName: 'Gaeilge' },
    { code: 'lt', name: 'Lithuanian', nativeName: 'Lietuvių' },
    { code: 'lv', name: 'Latvian', nativeName: 'Latviešu' },
    { code: 'mt', name: 'Maltese', nativeName: 'Malti' },
    { code: 'nb', name: 'Norwegian (Bokmål)', nativeName: 'Bokmål' },
    { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
    { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
    { code: 'cs', name: 'Czech', nativeName: 'Čeština' },
    { code: 'pl', name: 'Polish', nativeName: 'Polski' },
    { code: 'ro', name: 'Romanian', nativeName: 'Română' },
    { code: 'it', name: 'Italian', nativeName: 'Italiano' },
    { code: 'ru', name: 'Russian', nativeName: 'Русский' },
    { code: 'da', name: 'Danish', nativeName: 'Dansk' },
    { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  ];

  constructor(
    @Inject(LOCALE_ID) private localeId: string,
    private router: Router
  ) {
    this.currentLanguageSubject.next(this.localeId);
  }

  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  setLanguage(languageCode: string): void {
    if (this.supportedLanguages.some(lang => lang.code === languageCode)) {
      this.currentLanguageSubject.next(languageCode);
      this.saveLanguagePreference(languageCode);

      // For production, we would need to reload the app with the new locale
      // This would typically involve redirecting to a locale-specific URL
      this.reloadWithLocale(languageCode);
    }
  }

  private saveLanguagePreference(languageCode: string): void {
    localStorage.setItem('preferred-language', languageCode);
  }

  getStoredLanguagePreference(): string | null {
    return localStorage.getItem('preferred-language');
  }

  detectBrowserLanguage(): string {
    const browserLang = navigator.language || navigator.languages?.[0] || 'en-US';

    // Check if exact match exists
    const exactMatch = this.supportedLanguages.find(lang => lang.code === browserLang);
    if (exactMatch) {
      return exactMatch.code;
    }

    // Check for language family match (e.g., 'en-GB' -> 'en-US')
    const langCode = browserLang.split('-')[0];
    const familyMatch = this.supportedLanguages.find(lang => lang.code.split('-')[0] === langCode);
    if (familyMatch) {
      return familyMatch.code;
    }

    // Default to English
    return 'en-US';
  }

  initializeLanguage(): void {
    const storedLanguage = this.getStoredLanguagePreference();
    const browserLanguage = this.detectBrowserLanguage();

    // Priority: stored preference > browser language > default (en-US)
    const languageToUse = storedLanguage || browserLanguage;

    if (languageToUse !== this.getCurrentLanguage()) {
      this.setLanguage(languageToUse);
    }
  }

  private reloadWithLocale(locale: string): void {
    const currentUrl = this.router.url;

    // Persist preference
    this.saveLanguagePreference(locale);

    if (ENV.production) {
      const newUrl = `/${locale}${currentUrl}`;
      window.location.replace(newUrl);
      return;
    }

    // In development, just notify; dev server typically serves only default locale
    this.showLanguageChangeNotification(locale);
  }

  private showLanguageChangeNotification(locale: string): void {
    const languageName = this.getLanguageName(locale);
    console.log(`Language preference saved: ${languageName}. Language will change on next page reload in a full i18n setup.`);

    // You could show a toast notification here
    alert(`Language preference saved: ${languageName}. In a production setup with full i18n, this would immediately switch the interface language.`);
  }

  getLanguageName(code: string): string {
    const language = this.supportedLanguages.find(lang => lang.code === code);
    return language ? language.nativeName : code;
  }
}
